import React, { useEffect, useState } from "react";
import { Stack, Group, Text, Switch, Loader, Alert } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { notifyError } from "../../utils/notify";
import { useApp } from "../../context/AppContext";
import { getApiUrl, API_CONFIG } from "../../config/api";
import {
  MessageSettings,
  DEFAULT_MESSAGE_SETTINGS,
} from "../../types/messageTemplates";

interface ServiceMessageSettingsProps {
  /** Salon service UUID. Empty string while creating a not-yet-saved service. */
  salonServiceUuid: string;
  /** When true, switches render disabled (no manage-services permission). */
  readOnly?: boolean;
  /**
   * New-service (deferred) mode: the parent wizard owns the settings until the
   * service exists, then PUTs them after creation. When provided (service not
   * yet created), toggles report up via onPendingChange instead of saving.
   */
  pendingSettings?: MessageSettings;
  onPendingChange?: (settings: MessageSettings) => void;
}

const TOGGLES: {
  key: keyof MessageSettings;
  label: string;
  description: string;
}[] = [
  {
    key: "send_first_reminder",
    label: "First reminder",
    description: "Two days before the appointment.",
  },
  {
    key: "send_second_reminder",
    label: "Second reminder",
    description: "Two hours before the appointment.",
  },
  {
    key: "send_immediate_marketing_followup",
    label: "Marketing follow-up",
    description: "A thank-you / rebooking text after the visit.",
  },
];

export const ServiceMessageSettings: React.FC<ServiceMessageSettingsProps> = ({
  salonServiceUuid,
  readOnly = false,
  pendingSettings,
  onPendingChange,
}) => {
  const { fetchWithAuth } = useApp();
  const isNew = !salonServiceUuid;
  const [settings, setSettings] = useState<MessageSettings>(
    DEFAULT_MESSAGE_SETTINGS,
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // When the initial GET fails for an existing service we must NOT let the user
  // toggle: the UI would show the all-on defaults and any PUT we sent would
  // silently overwrite the (unknown) persisted state. Disable + show an alert.
  const [loadError, setLoadError] = useState(false);
  // 403/404 means the backend hasn't enabled per-service messaging for this
  // salon at all — not a transient failure. The section now renders for every
  // business vertical, so treat "not available" as "render nothing" instead of
  // shouting an error toast at salons that never asked for the feature.
  const [unavailable, setUnavailable] = useState(false);

  // Existing service: load the persisted toggles.
  useEffect(() => {
    if (isNew) return;
    let active = true;
    (async () => {
      setLoading(true);
      setLoadError(false);
      setUnavailable(false);
      try {
        const url = getApiUrl(
          API_CONFIG.ENDPOINTS.SERVICE_MESSAGE_SETTINGS,
          "",
          { salonServiceUuid },
        );
        const data = await fetchWithAuth(url);
        if (active && data?.message_settings) {
          setSettings({
            ...DEFAULT_MESSAGE_SETTINGS,
            ...data.message_settings,
          });
        }
      } catch (err) {
        if (!active) return;
        const status = (err as { status?: number } | null)?.status;
        if (status === 403 || status === 404) {
          setUnavailable(true);
          return;
        }
        console.error("Failed to load message settings:", err);
        setLoadError(true);
        notifyError(
          "Toggles are disabled until reload to avoid overwriting saved values.",
          { title: "Could not load message settings" },
        );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isNew, salonServiceUuid, fetchWithAuth]);

  // New-service mode reflects the parent's pending state; otherwise local state.
  const current = isNew
    ? (pendingSettings ?? DEFAULT_MESSAGE_SETTINGS)
    : settings;

  const handleToggle = async (key: keyof MessageSettings, checked: boolean) => {
    if (isNew) {
      onPendingChange?.({ ...current, [key]: checked });
      return;
    }

    // Optimistic functional update so concurrent toggles compose. On failure
    // revert ONLY the affected key, leaving other in-flight toggles intact.
    setSettings((prev) => ({ ...prev, [key]: checked }));
    setSaving(true);
    try {
      const url = getApiUrl(API_CONFIG.ENDPOINTS.SERVICE_MESSAGE_SETTINGS, "", {
        salonServiceUuid,
      });
      // Build the PUT body from the latest committed state at send time so an
      // in-flight save reflects any toggles that landed before it.
      const body = { ...settings, [key]: checked };
      await fetchWithAuth(url, {
        method: "PUT",
        body: JSON.stringify(body),
      });
    } catch (err) {
      console.error("Failed to update message settings:", err);
      setSettings((prev) => ({ ...prev, [key]: !checked }));
      notifyError("Your change was not saved. Please try again.", {
        title: "Could not update messages",
      });
    } finally {
      setSaving(false);
    }
  };

  // Backend says this salon has no per-service messaging: render nothing.
  if (unavailable) return null;

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <Text fw={600} size="sm">
          Automated messages
        </Text>
        {(loading || saving) && <Loader size="xs" />}
      </Group>
      <Text size="xs" c="dimmed">
        Choose which automated texts clients receive for this service. All are
        on by default.
      </Text>
      {loadError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Could not load saved message settings. Refresh to try again — toggles
          are disabled until then so saved values aren't overwritten.
        </Alert>
      )}
      {TOGGLES.map((t) => (
        <Switch
          key={t.key}
          label={t.label}
          description={t.description}
          checked={current[t.key]}
          onChange={(event) => handleToggle(t.key, event.currentTarget.checked)}
          disabled={readOnly || loading || saving || loadError}
          color="var(--brand-teal)"
        />
      ))}
    </Stack>
  );
};
